"use client";

import { useState, useRef, useEffect, type JSX } from "react";
import Link from "next/link";
import { ChevronDownIcon, MenuIcon, XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SPECIALIST_URL } from "@/lib/consultation-links";

const dropdownNavItems = [
  {
    label: "Incorporation",
    children: [
      { label: "Company Formation", href: "/hong-kong-company-formation" },
      { label: "Banking Setup", href: "/hong-kong-business-banking" },
    ],
  },
  {
    label: "Accounts",
    children: [
      { label: "Bookkeeping", href: "/bookkeeping-services-hong-kong" },
      { label: "Tax Optimization", href: "/tax-optimization" },
          { label: "Accounting", href: "/accounting-services-hong-kong" },
    ],
  },
  {
    label: "Payments",
    children: [
      { label: "Hong Kong Setup", href: "/hongkong-setup" },
      { label: "USA LLC", href: "/usa-llc" },
    ],
  },
];

const simpleNavItems = [
  { label: "Corporate Secretary", href: "/corporate-secretary" },
  { label: "About", href: "/about" },
];

export type NavTheme = "light" | "dark";

/**
 * Per-theme class strings. The `light` values are copied verbatim from what
 * this component rendered before the `dark` variant existed, so the light path
 * — every page under app/(marketing) — is unchanged.
 *
 * `dark` is used only by the home-v3 landing page at app/(home)/page.tsx,
 * which is bg-black.
 */
const NAV_THEME: Record<NavTheme, Record<string, string>> = {
  light: {
    bar: "bg-[#F5F5F5] shadow-sm",
    logoWord: "",
    navItem: "text-[#040015] transition-colors hover:bg-black/5",
    panel: "bg-white shadow-[0px_8px_24px_rgba(0,0,0,0.12)]",
    panelLink: "text-[#040015] transition-colors hover:bg-[#f5f7ff] hover:text-[#0f6dfa]",
    contactSales: "text-[#000000]",
    hamburger: "text-black hover:bg-black/5",
    mobileOverlay: "border-gray-200 bg-[#F5F5F5]",
    mobileItemBorder: "border-gray-100",
    mobileAccordionBtn: "text-[#040015]",
    mobileLink: "text-[#040015] hover:text-[#0f6dfa]",
    mobileDivider: "border-gray-200",
    loginBtn: "text-black",
  },
  dark: {
    bar: "bg-black",
    // Applied to the wordmark only. path-11.svg is a single flat #1E1E1E fill,
    // so brightness(0) invert(1) makes it pure white — no second asset needed.
    // The icon mark (path-10.png) is deliberately left alone: it is brand blue
    // (#3371FF), which reads fine on black, and inverting would flatten it to
    // solid white and lose the brand colour.
    logoWord: "[filter:brightness(0)_invert(1)]",
    navItem: "text-white transition-colors hover:bg-white/10",
    panel: "border border-white/10 bg-[#0f1218] shadow-[0px_8px_24px_rgba(0,0,0,0.5)]",
    panelLink: "text-white/90 transition-colors hover:bg-white/5 hover:text-[#6fa6ff]",
    contactSales: "text-white",
    hamburger: "text-white hover:bg-white/10",
    mobileOverlay: "border-white/10 bg-[#0f1218]",
    mobileItemBorder: "border-white/10",
    mobileAccordionBtn: "text-white",
    mobileLink: "text-white/90 hover:text-[#6fa6ff]",
    mobileDivider: "border-white/10",
    loginBtn: "text-white",
  },
};

// Shared timer ref passed from parent so switching menus is instant
function DropdownItem({
  item,
  activeMenu,
  setActiveMenu,
  sharedTimer,
  t,
}: {
  item: (typeof dropdownNavItems)[number];
  activeMenu: string | null;
  setActiveMenu: (label: string | null) => void;
  sharedTimer: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;
  t: Record<string, string>;
}) {
  const open = activeMenu === item.label;

  const cancelClose = () => {
    if (sharedTimer.current) clearTimeout(sharedTimer.current);
  };

  const handleEnter = () => {
    cancelClose();
    setActiveMenu(item.label); // instant switch, no delay
  };

  const handleLeave = () => {
    cancelClose();
    sharedTimer.current = setTimeout(() => setActiveMenu(null), 300);
  };

  return (
    <li className="relative">
      <button
        type="button"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onClick={() => setActiveMenu(open ? null : item.label)}
        className={`inline-flex items-center gap-1 whitespace-nowrap rounded-[10px] px-3 py-2 font-dm-sans text-sm font-semibold leading-7 tracking-[0] ${t.navItem}`}
      >
        {item.label}
        <ChevronDownIcon
          className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown panel */}
      <div
        onMouseEnter={cancelClose}
        onMouseLeave={handleLeave}
        className={`absolute left-0 top-full z-50 mt-1 min-w-[180px] overflow-hidden rounded-[14px] ${t.panel} transition-all duration-200 ${
          open ? "pointer-events-auto translate-y-0 opacity-100" : "pointer-events-none -translate-y-1 opacity-0"
        }`}
      >
        <ul className="flex flex-col py-2">
          {item.children.map((child) => (
            <li key={child.href}>
              <Link
                href={child.href}
                className={`block px-5 py-3 font-dm-sans text-sm font-semibold ${t.panelLink}`}
                onClick={() => setActiveMenu(null)}
              >
                {child.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
}

export const PrimaryNavigationSection = ({
  theme = "light",
}: {
  theme?: NavTheme;
} = {}): JSX.Element => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const sharedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const t = NAV_THEME[theme];

  return (
    <>
      {/* Promo banner — scrolls with page */}
      {/* <div className="w-full bg-[linear-gradient(90deg,rgba(123,27,15,1)_0%,rgba(191,33,23,1)_45%,rgba(248,168,50,1)_100%)]">
        <div className="mx-auto flex w-full max-w-[1440px] items-center justify-between gap-3 px-4 py-2 sm:px-6 sm:py-[10px] lg:px-9 lg:py-[15px]">
          <div className="flex min-w-0 flex-1 flex-col items-center justify-center">
            <p className="font-montserrat text-center text-xs leading-snug tracking-[-0.20px] text-white sm:text-base lg:text-lg lg:leading-[27.3px]">
              <span className="inline-flex h-[22px] items-center bg-[#ffd66e] px-1.5 text-[13px] font-semibold text-black sm:h-[25px] sm:px-2 sm:text-[21px]">
                Free Incorporation
              </span>
              <span className="italic tracking-[-0.04px]"> when bundled with accounting and audit, </span>
              <span className="font-semibold tracking-[-0.04px]">Limited offer – </span>
              <span className="font-semibold text-[#ffcd65]">6</span>
              <span className="text-[#ffcd65]">/100 </span>
              <span>slots already claimed!</span>
            </p>
            <button
              type="button"
              className="mt-0.5 h-7 font-dm-sans text-[13px] font-medium leading-7 tracking-[-0.20px] text-[#fff6df] underline"
            >
              T&amp;C&apos;s apply
            </button>
          </div>
          <Button
            type="button"
            className="hidden h-auto shrink-0 rounded-[99px] bg-[#ffd66e] px-4 py-1.5 font-montserrat text-base font-bold leading-7 text-black shadow-[0px_0px_10px_#00000066] hover:bg-[#ffd66e]/90 sm:flex lg:px-5 lg:py-2 lg:text-lg"
          >
            Register Now
          </Button>
        </div>
      </div> */}

      {/* Nav bar — sticky. z-[60], not z-50: ClientTrustAndTestimonialsSection
          (the pre-footer section that hides StickyBottomCta) is also z-50 and
          opaque, and since it's later in the DOM, it wins ties at the same
          z-index — hiding the nav whenever that section scrolls up to occupy
          the same on-screen region as this sticky bar. Needs to stay above
          both that z-50 and StickyBottomCta's z-40. */}
      <div className={`sticky top-0 z-[60] w-full ${t.bar}`}>
        <div className="mx-auto flex min-h-16 w-full max-w-[1330px] items-center justify-between gap-2 px-4 py-2 sm:px-6 xl:min-h-20 xl:px-[40px] xl:py-[10.5px]">

          {/* Logo */}
          <div className="flex min-w-0 items-center gap-4 xl:gap-8">
            <Link href="/" aria-label="Company home" className="shrink-0">
              <div className="relative h-[24px] w-36 sm:h-[30px] sm:w-44">
                {/* Icon mark — brand blue, unfiltered in both themes */}
                <img
                  className="absolute left-0 top-0 h-full w-[20.38%]"
                  alt="Path"
                  src="https://c.animaapp.com/mq559owgmM3hEE/img/path-10.png"
                />
                {/* Wordmark — near-black, inverted to white on the dark theme */}
                <img
                  className={cn(
                    "absolute left-[26.11%] top-[18.36%] h-[62.53%] w-[73.89%]",
                    t.logoWord,
                  )}
                  alt="Path"
                  src="https://c.animaapp.com/mq559owgmM3hEE/img/path-11.svg"
                />
              </div>
            </Link>

            {/* Desktop nav */}
            <nav aria-label="Primary navigation" className="hidden xl:block">
              <ul className="flex items-center gap-1">
                {dropdownNavItems.map((item) => (
                  <DropdownItem
                    key={item.label}
                    item={item}
                    activeMenu={activeMenu}
                    setActiveMenu={setActiveMenu}
                    sharedTimer={sharedTimer}
                    t={t}
                  />
                ))}
                {simpleNavItems.map((item) => (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      className={`inline-flex items-center whitespace-nowrap rounded-[10px] px-3 py-2 font-dm-sans text-sm font-semibold leading-7 tracking-[0] ${t.navItem}`}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Right-side actions */}
          <div className="flex items-center gap-2 xl:gap-3">
             {/* <Link
              href="/contact"
              className={`hidden items-center gap-1.5 font-dm-sans text-base font-semibold leading-7 tracking-[0] xl:flex ${t.contactSales}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.84a16 16 0 0 0 6.29 6.29l1.46-1.46a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
              Contact Sales
            </Link> */}
            <Link
              href={SPECIALIST_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="h-auto rounded-[20px] border-2 border-solid bg-[#0f6dfa] px-3 py-2 font-montserrat text-sm font-semibold leading-6 text-white hover:bg-[#0f6dfa]/90 sm:px-4 xl:px-[15px] xl:py-[10px] xl:text-base xl:leading-7"
            >
              Get started
            </Link>
           
            {/* <button
              type="button"
              className="hidden border-b-2 border-black font-dm-sans text-base font-semibold leading-6 tracking-[0] text-black xl:block"
            >
              Log in
            </button>
            <button
              type="button"
              className="hidden items-center gap-1.5 rounded-[10px] py-1.5 pl-2.5 pr-[4.31px] xl:inline-flex"
            >
              <img className="h-[22px] w-[22px]" alt="Hk flag" src="https://c.animaapp.com/mq559owgmM3hEE/img/hk-flag.svg" />
              <span className="font-dm-sans text-base font-semibold leading-6 tracking-[0] text-black">HK</span>
              <ChevronDownIcon className="h-4 w-4 text-black" />
            </button> */}

            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className={`flex items-center justify-center rounded-lg p-1.5 xl:hidden ${t.hamburger}`}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
            >
              {mobileOpen ? <XIcon className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile/tablet/lg slide-down overlay */}
        <div
          className={`absolute left-0 right-0 top-full z-40 overflow-hidden border-t ${t.mobileOverlay} shadow-lg transition-all duration-300 ease-in-out xl:hidden ${
            mobileOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <nav aria-label="Mobile navigation" className="px-4 pb-5 pt-1 sm:px-6">
            <ul className="flex flex-col">
              {/* Dropdown items with accordion */}
              {dropdownNavItems.map((item) => (
                <li key={item.label} className={`border-b ${t.mobileItemBorder}`}>
                  <button
                    type="button"
                    onClick={() =>
                      setMobileExpanded((prev) =>
                        prev === item.label ? null : item.label
                      )
                    }
                    className={`flex w-full items-center justify-between py-3 font-dm-sans text-base font-semibold tracking-[0] ${t.mobileAccordionBtn}`}
                  >
                    {item.label}
                    <ChevronDownIcon
                      className={`h-4 w-4 transition-transform duration-200 ${
                        mobileExpanded === item.label ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-200 ${
                      mobileExpanded === item.label ? "max-h-40" : "max-h-0"
                    }`}
                  >
                    <ul className="flex flex-col pb-2 pl-3">
                      {item.children.map((child) => (
                        <li key={child.href}>
                          <Link
                            href={child.href}
                            className={`block py-2.5 font-dm-sans text-sm font-medium ${t.mobileLink}`}
                            onClick={() => setMobileOpen(false)}
                          >
                            {child.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
              ))}

              {/* Simple items */}
              {simpleNavItems.map((item) => (
                <li key={item.href} className={`border-b ${t.mobileItemBorder}`}>
                  <Link
                    href={item.href}
                    className={`block py-3 font-dm-sans text-base font-semibold tracking-[0] ${t.mobileLink}`}
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Secondary links */}
            <div className={`mt-4 flex flex-col gap-3 border-t pt-4 ${t.mobileDivider}`}>
              {/* <Link
                href="/contact"
                className="font-dm-sans text-base font-semibold leading-7 tracking-[0] text-[#0066ff]"
                onClick={() => setMobileOpen(false)}
              >
                Contact Sales
              </Link> */}
              <button
                type="button"
                className={`text-left font-dm-sans text-base font-semibold leading-6 tracking-[0] ${t.loginBtn}`}
              >
                Log in
              </button>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
};
